// @ts-ignore
import { useState, useRef, useEffect, use, useMemo, memo, useCallback } from 'react';
import { TextField, Card, CardContent, Typography, Box, IconButton, Button, MenuItem, Select, FormControlLabel, Checkbox, Tooltip } from '@mui/material';
import Scrollbars from 'rc-scrollbars';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PrintIcon from '@mui/icons-material/Print';
import { errorTextColor, hoverStyle, iconColor, successTextColor, warningTextColor } from 'styles/main';
import { useLocalNotifications } from './Events';
import usePreventDoubleClick from './AntiDBC';
import { CancelButton, OKButton } from './EIComponents/LittleComponents';
import { useReactToPrint } from 'react-to-print';
import { get, post } from 'api/apiBridges/httpBridge';
import useCapsLock from './checkCaps';
import { addGSSeparators, EXPECTED_LENGTHS, getSectionColor, getSectionDisplay, getSectionLabel, parseCode, useQzTray, useSound } from './KIZFunctions';
import useCopyToClipboard from './copyHook';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { ShowModal } from './Modal';
import { useVirtualizer } from '@tanstack/react-virtual';

export const Scanner = ({inputCodes, onCancel, onSave, onChange}) => {
  /** @type {[any, Function]} */
  const [codes, setCodes] = useState([]);
  /** @type {[any, Function]} */
  const [inputValue, setInputValue] = useState('');
  /** @type {any} */
  const inputRef = useRef(null);

  const { playSuccess, playError } = useSound('/sounds/scan-success.mp3', '/sounds/scan-error.mp3');

  // Фокус при монтировании
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (codes.length === 0) {
      let tmp = []
      for (let code of inputCodes) {
        const parsed = parseCode(code);
        tmp.push({ full: code, parsed })
      }
      setCodes(tmp)
    }
  }, [inputCodes]);

  const focusInput = () => {
    inputRef.current?.focus();
  };

  const { showNotification } = useLocalNotifications();
  const preventDoubleClick = usePreventDoubleClick();
  const isCapsLockOn = useCapsLock();
  // @ts-ignore
  const { copy, copied } = useCopyToClipboard();


  // ----- Добавление кода в список -----
  const handleAddCode = () => {
    const trimmed = inputValue.trim();

    if (trimmed === '') return;

    if (isCapsLockOn) {
      showNotification({
        id: 'capslockErr',
        type: 'warning',
        message: `Не используйте сканнер с включенным CapsLock`
      });
      playError()
      setInputValue('');
      focusInput();
      return
    }

    const hasRussianChars = (str) => {
      const russianRegex = /[а-яА-ЯёЁ]/;
      return russianRegex.test(str);
    };

    if (hasRussianChars(trimmed)) {
      showNotification({
        id: 'rassistErr',
        type: 'warning',
        message: `Переключите раскладку на английскую`
      });
      playError()
      setInputValue('');
      focusInput();
      return
    }

    

    // Автоматически добавляем GS, если их нет
    // const codeWithGS = ensureGS(trimmed);
    let normal = addGSSeparators(trimmed)
  

    const parsed = parseCode(normal);
    console.log("parsed", parsed)

    // Генерация рандомного
    // const trimmed = generateRandomCode();
    // const parsed = parseCode(trimmed);

    // Проверка: есть ли уже такой код в списке?
    if (codes.some((item) => item.full === trimmed)) {
      showNotification({
        id: 'dublicate', 
        type: 'warning', 
        message: 'Такой код уже есть' 
      });
      playError()
      setInputValue('');
      focusInput();
      return;
    }

    setCodes((prev) => [...prev, { full: trimmed, parsed }]);
    onChange(true)
    showNotification({
      id: 'SCaddKIZ',
      type: 'success',
      message: 'КИЗ успешно добавлен'
    });
    playSuccess()
    setInputValue(''); // очищаем поле
    focusInput();
  };

  const handleDelete = (code) => {
    setCodes((prev) => prev.filter((fel) => fel.full !== code));
    onChange(true)
  };

  const cancel = () => {
    onCancel()
  }

  const save = () => {
    let tmp = []
    for (let code of codes) {
      tmp.push(code.full)
    }
    onChange(false)
    onSave(tmp)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAddCode();
    }
  };

  const handleContainerClick = () => {
    focusInput();
  };

  

  const getSectionColor = (key, value) => {
    // Если секция отсутствует
    if (value === null) {
      const isCritical = key === '01' || key === '21';
      return isCritical ? '#ff7978' : '#febf58';
    }


    return '#078376';
    // // Если секция присутствует – проверяем длину
    // const expected = EXPECTED_LENGTHS[key];
    // if (value.length === expected) {
    //   return '#078376';
    // }
    // // Неверная длина – всегда красный
    // return '#ff7978';
  };

  const getSectionDisplay = (key, value) => {
    const expected = EXPECTED_LENGTHS[key];
    if (value === null) {
      return '_'.repeat(expected);
    }
    if (value.length < expected) {
      return value + '_'.repeat(expected - value.length);
    }
    // Если длина >= ожидаемой – показываем как есть (лишние символы тоже видны)
    return value;
  };

  const getSectionLabel = (key) => {
    const map = {
      '01': 'GTIN',
      '21': 'ИСН',
      '91': 'Идентификатор',
      '92': 'Код проверки',
    };
    return map[key] || key;
  };

  const handleCopy = async (text) => {
    const success = await copy(text);
    if (success) {
      showNotification({
        id: 'copyCodeSucces',
        type: 'success',
        message: 'Код скопирован'
      });
    }
  };

  return (
    <Box onClick={handleContainerClick} sx={{ width: "100%", height: "100%", display: 'flex', flexDirection: 'column' }}>
      <TextField
        inputRef={inputRef}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        fullWidth
        placeholder="Просканируйте код"
        variant="standard"
        onBlur={() => setTimeout(focusInput, 100)}
        autoComplete="off"
        sx={{
            width: "40%",
            borderRadius: '10px',
            backgroundColor: '#F5F9FA',
            marginLeft: '10px',
            marginBottom: '10px',
            paddingLeft: "16px",
            '& .MuiInput-underline': {
                '&:before': { borderBottom: 'none' },
                '&:after': { borderBottom: 'none' },
                '&:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
            },
        }}
      />

      <Box sx={{height: "90%", width: '100%'}}>
        <Scrollbars style={{height: '100%', width: "100%", display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            {codes.map((item, index) => (
                <Card key={index} sx={{borderRadius: '10px', margin: '10px', boxShadow: '0px 0px 5px rgba(0,0,0,0.2)'}}>
                    <CardContent sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'}}>
                      <Box sx={{display: 'flex', flexDirection: 'column'}}>
                        {/* Секции с подписями сверху */}
                        <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                            {['01', '21', '91', '92'].map((key) => {
                            const value = item.parsed[key];
                            const color = getSectionColor(key, value);
                            const label = getSectionLabel(key);
                            const display = getSectionDisplay(key, value);

                            return (
                              <Box key={key} component="span" sx={{mr: 1 }}>
                                  <Typography
                                      variant="caption"
                                      component="span"
                                      sx={{ fontWeight: 'bold', color}}
                                  >
                                      {label}:
                                  </Typography>
                                  <Typography
                                      variant="body2"
                                      component="span"
                                      sx={{ fontFamily: 'monospace', color: 'gray'}}
                                  >
                                      {display}
                                  </Typography>
                              </Box>
                            );
                            })}
                        </Box>
                        {/* Полный код (для справки) */}
                        <div style={{display: 'flex', flexDirection: 'row', alignItems: 'flex-start'}}>
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'nowrap',
                              color: 'gray',
                              mt: '1px'
                            }}
                          >
                            Полный код:
                          </Typography>
                          <Button
                            variant="text"
                            sx={{
                              wordBreak: 'break-all',
                              color: 'gray',
                              fontSize: '14px',
                              padding: 0,
                              margin: 0,
                              textTransform: 'none',
                              ml: '5px',
                              textAlign: 'left'
                            }}
                            onClick={() => { handleCopy(item.full) }}
                          >
                            {item.full}
                          </Button>
                        </div>
                      </Box>
                      <IconButton 
                          aria-label="menu" 
                          // @ts-ignore
                          onClick={() => { handleDelete(item.full) }}

                          sx={{marginRight: '10px'}}
                      >
                          <DeleteForeverIcon
                              sx={{
                                  transition: 'all 0.3s ease',
                                  '&:hover': hoverStyle,
                                  color: iconColor
                              }}
                          />
                      </IconButton>
                    </CardContent>
                </Card>
                ))}
        </Scrollbars>
      </Box>
      <Box
          sx={{
              display: "flex",
              gap: 2,
              width: "calc(100% - 20px)",
              margin: "0px 10px 0px 10px",
              justifyContent: 'space-between',
              alignItems: 'flex-end'
          }}
      >
        
          <CancelButton action={cancel} text={"ОТМЕНА"}/>
          <OKButton action={preventDoubleClick(save, 1000)} text={"СОХРАНИТЬ"}/>
          
      </Box>
    </Box>
  );
};


//Функция максимально адаптирована под fbs WB
export const ScannerSplit = ({ inputCodes, bclist, onCancel, onSave, onChange, order_barcodes, cli, market, autoSaveKey }) => {
  /** @type {[any, Function]} */
  const [codes, setCodes] = useState({});

  /** @type {[any, Function]} */
  const [inputValue, setInputValue] = useState('');

  /** @type {[any, Function]} */
  const [selectedBC, setSelectedBC] = useState(
    bclist?.length > 0 ? bclist[0] : ''
  );

  /** @type {any} */
  const inputRef = useRef(null);

  const printContainerRef = useRef(null);

  /** @type {[any, Function]} */
  const [printTrigger, setPrintTrigger] = useState(undefined);
  // @ts-ignore
  // @ts-ignore
  const [loadingPrint, setLoadingPrint] = useState(false);

  
  /** @type {[any, Function]} */
  // @ts-ignore
  const [qrChecked, setQrChecked] = useState(undefined);

  // ----- Ключи для localStorage -----
  const STORAGE_KEYS = {
    SOUND: 'scanner_split_sound_enabled',
    AUTO_PRINT: 'scanner_split_auto_print_enabled'
  };

  // ----- Состояния для чекбоксов с загрузкой из localStorage -----
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.SOUND);
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [autoPrintEnabled, setAutoPrintEnabled] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.AUTO_PRINT);
    return saved !== null ? JSON.parse(saved) : true;
  });

  // ----- Сохранение в localStorage при изменении -----
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.SOUND, JSON.stringify(soundEnabled));
  }, [soundEnabled]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.AUTO_PRINT, JSON.stringify(autoPrintEnabled));
  }, [autoPrintEnabled]);

  const { showNotification } = useLocalNotifications();


  const preventDoubleClick = usePreventDoubleClick();
  const isCapsLockOn = useCapsLock();
  // @ts-ignore
  const { copy } = useCopyToClipboard();

  const { playSuccess, playError } = useSound('/sounds/scan-success.mp3', '/sounds/scan-error.mp3');
  const { printLocal } = useQzTray();

  const ROW_HEIGHT = 160; // финальная высота строки включая gap
  /** @type {any} */
  const parentRef = useRef(null);

  // Фокус при монтировании
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const initializedRef = useRef(false);

  const codeSetRef = useRef(new Set());

  /** @type {any} */
  const usedOrdersRef = useRef(new Set());

  const onChangeRef = useRef(onChange);
  const onSaveRef = useRef(onSave);
  const onCancelRef = useRef(onCancel);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onSaveRef.current = onSave; }, [onSave]);
  useEffect(() => { onCancelRef.current = onCancel; }, [onCancel]);

  const checkQRStatuses = useCallback(async (orders) => {
    if (market !== "wb") return;
    const res = await post("WBAPI/getSupplyQRS", { uid: cli, orders });
    const result = {};
    Object.keys(res).forEach((k) => {
      switch (res[k]) {
        case "filled":
        case "optional":
        case "sgtinSoldB2B":
        case "sgtinIntroduced":
          result[k] = { status: res[k], text: "Проверка пройдена", color: successTextColor };
          break;
        case "deadlineExceeded":
        case "pending":
        case "":
          result[k] = { status: res[k], text: "Проверка в процессе", color: warningTextColor };
          break;
        default:
          result[k] = { status: res[k], text: "Проверка провалена", color: errorTextColor };
      }
    });
    setQrChecked(result);
  }, [market, cli]);

  //Функция преобразовывает order_barcod в map, который отдает список order по bc
  const orderBarcodesIndex = useMemo(() => {
    const map = new Map();
    for (const ob of order_barcodes) {
      if (!map.has(ob.barcode)) map.set(ob.barcode, []);
      map.get(ob.barcode).push(ob.order);
    }
    return map;
  }, [order_barcodes]);

  useEffect(() => {
    // console.log('inputCodes', inputCodes)
    // Ждём, пока придут данные
    if (!bclist?.length || !inputCodes || typeof inputCodes !== 'object') {
      return;
    }

    // Инициализируем только один раз
    if (initializedRef.current) {
      return;
    }

    const tmp = {};

    let orders = []

    codeSetRef.current.clear();

    for (const bc of bclist) {
      const bcCodes = inputCodes[bc] || [];

      tmp[bc] = bcCodes.map((items) => {
        
        codeSetRef.current.add(items.code);

        if (!orders.includes(items.order)) {orders.push(items.order)}

        usedOrdersRef.current.add(items.order);

        return {
          full: items.code,
          parsed: parseCode(items.code),
          order: items.order
        }
      });
    }

    checkQRStatuses(orders)
    const autoSave = localStorage.getItem(autoSaveKey)
    console.log("autoSave", autoSave)
    if (autoSave) {
      ShowModal(
          'Найдено автоматическое сохранение',
          <div>У вас есть автоматическое сохранение, хотите восстановить его?</div>,
          async () => {
              const restored = JSON.parse(autoSave);
              codeSetRef.current.clear();
              usedOrdersRef.current.clear();
              for (const bc of Object.keys(restored)) {
                for (const item of restored[bc]) {
                  codeSetRef.current.add(item.full);
                  usedOrdersRef.current.add(item.order);
                }
              }
              setCodes(restored);
          }
      );
      setCodes(tmp);
    }
    else {
      setCodes(tmp);
    }
    initializedRef.current = true;
  }, [inputCodes, bclist, market, cli, autoSaveKey, checkQRStatuses]);

  // Если bclist изменился и выбранного элемента больше нет
  useEffect(() => {
    if (bclist?.length > 0 && !bclist.includes(selectedBC)) {
      // setSelectedBC(bclist[0]);
      setSelectedBC(undefined);
    }
  }, [bclist, selectedBC]);

  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem(autoSaveKey, JSON.stringify(codes));
    }, 500);

    return () => clearTimeout(timer);
  }, [codes, autoSaveKey]);

  const focusInput = () => {
      requestAnimationFrame(() => {
          setTimeout(() => {
              inputRef.current?.focus();
          }, 0);
      });
  };

  // Текущие коды выбранного элемента
  const currentCodes = codes[selectedBC] || [];

  //Храним список всех кодов, чтобы каждый раз не создавать его заново
  const allCodes = useMemo(() => {
      return Object.values(codes).flat();
  }, [codes]);

  //Сразу понимаем, какие коды отображать, меняется при каждом рендере
  const allVisibleCodes = selectedBC ? currentCodes : allCodes;

  //Функция поиска свободного ордера
  const findNextFreeOrder = useCallback((barcode) => {
    const used = usedOrdersRef.current;

    if (barcode) {
      const list = orderBarcodesIndex.get(barcode);
      if (!list) return null;
      for (const order of list) {
        if (!used.has(order)) return { order, barcode };
      }
      return null;
    }

    // !selectedBC — ищем первый свободный по всем
    for (const ob of order_barcodes) {
      if (!used.has(ob.order)) return { order: ob.order, barcode: ob.barcode };
    }
    return null;
  }, [orderBarcodesIndex, order_barcodes]);

  // ----- Проверка, является ли введенное значение баркодом из списка -----

  const bclistSet = useMemo(() => new Set(bclist ?? []), [bclist]);
  const isBarcodeFromList = useCallback((value) => bclistSet.has(value), [bclistSet]);

  // ----- Добавление кода в список -----
  const handleAddCode = () => {
    const trimmed = inputValue.trim();

    if (trimmed === '') return;

    if (isCapsLockOn) {
      showNotification({
        id: 'capslockErr',
        type: 'warning',
        message: `Не используйте сканнер с включенным CapsLock`
      });
      if (soundEnabled) playError();
      setInputValue('');
      focusInput();
      return
    }

    const hasRussianChars = (str) => {
      const russianRegex = /[а-яА-ЯёЁ]/;
      return russianRegex.test(str);
    };

    if (hasRussianChars(trimmed)) {
      showNotification({
        id: 'rassistErr',
        type: 'warning',
        message: `Переключите раскладку на английскую`
      });
      if (soundEnabled) playError();
      setInputValue('');
      focusInput();
      return
    }

    // Проверяем, является ли введенное значение баркодом из списка
    if (isBarcodeFromList(trimmed)) {
      // Если это баркод из списка - переключаем на него
      setSelectedBC(trimmed);
      setInputValue('');
      focusInput();
      
      showNotification({
        id: 'barcode-switch',
        type: 'info',
        message: `Переключено на баркод: ${trimmed}`
      });
      if (soundEnabled) playSuccess();
      return;
    }

    // Автоматически добавляем GS, если их нет
    let normal = addGSSeparators(trimmed)
  
    const parsed = parseCode(normal);

    // Проверка дубля только внутри выбранного элемента bclist
    // if (Object.values(codes).flat().some((item) => item.full === normal)) {
    //Теперь проверяем по set
    if (codeSetRef.current.has(normal)) {
      showNotification({
        id: 'dublicate',
        type: 'warning',
        message: 'Такой код уже есть'
      });
      if (soundEnabled) playError();
      setInputValue('');
      focusInput();

      return;
    }

    // let usedOrders = []
    //Теперь это set, чтобы не перебирать каждый раз
    // const usedOrders = new Set();
    
    const next = findNextFreeOrder(selectedBC);

    if (next) {
      
      //Добавляем новый код в set
      codeSetRef.current.add(normal);

      usedOrdersRef.current.add(next.order);
      
      setCodes((prev) => ({
        ...prev,
        [next.barcode]: [
          {
            full: normal,
            parsed,
            order: next.order
          },
          ...(prev[next.barcode] || []),   // ← новый в начало
        ]
      }));
      // onChange(true);
      onChangeRef.current(true); 
      showNotification({
        id: 'SCaddKIZ',
        type: 'success',
        message: 'КИЗ успешно добавлен'
      });
      if (soundEnabled) playSuccess();
      
      if (autoPrintEnabled) {
        // await handleQzPrint(next.order);
        handleQzPrint(next.order).catch(err => console.error(err));  // не дожидаемся
      }
      
      setInputValue('');
      focusInput();
    }
    else {
      showNotification({
        id: 'noOrder',
        type: 'warning',
        message: 'Вы пытаетесь загрузить больше КИЗов, чем есть заказов для этого товара'
      });
      if (soundEnabled) playError();
    }

  };

  // useEffect(() => {
  //   requestAnimationFrame(() => {
  //     // scrollContainerRef.current?.scrollToBottom();
  //     scrollContainerRef.current?.scrollToTop?.();
      
  //   });
  // }, [currentCodes.length]);

  // @ts-ignore
  const handleDelete = useCallback((code, order) => {
    codeSetRef.current.delete(code);
    usedOrdersRef.current.delete(order);

    //Так как коды уникальные всегда, то к какому бы баркоду не относился код, он удалится
    setCodes((prev) => {
      const tmp = {};
      for (const k of Object.keys(prev)) {
        tmp[k] = prev[k].filter((item) => item.full !== code);
      }
      return tmp;
    });

    // onChange(true);
    onChangeRef.current(true);
  }, []);

  const cancel = () => {
    // onCancel();
    onCancelRef.current()
  };



  //Используется только в save, поэтму объявляем тут
  const codesRef = useRef(codes);
  useEffect(() => { codesRef.current = codes; }, [codes]);

  const save = useCallback(() => {
    const currentCodes = codesRef.current;
    const result = {};
    for (const bc of bclist || []) {
        result[bc] = (currentCodes[bc] || []).map(item => ({ code: item.full, order: item.order }));
    }
    onSaveRef.current(result);
}, [bclist]);

  const saveAction = useMemo(
    () => preventDoubleClick(save, 1000),
    [preventDoubleClick, save]
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAddCode();
    }
  };

  const handleContainerClick = () => {
    focusInput();
  };
  

  const startPrint = useReactToPrint({
      contentRef: printContainerRef,

      pageStyle: `
        @page {
            size: 58mm 40mm;
            margin: 0 !important;
            padding: 0 !important;
            marks: none;
        }

        body {
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: white;
        }

        html, body {
            height: auto !important;
            overflow: visible !important;
        }

        * {
            box-sizing: border-box;
        }

        .barcode-print-page {
            width: 58mm;
            height: 40mm;
            page-break-after: always;
            break-after: page;
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .barcode-print-page:last-child {
            page-break-after: auto;
            break-after: auto;
        }
    `,

      documentTitle: 'Компонент',

      onAfterPrint: () => {
          setLoadingPrint(false);
          setPrintTrigger(undefined);
      }
  });

  // @ts-ignore
  const handlePrint = useCallback(async (order) => {
      setLoadingPrint(true);
      let files = await get("WBAPI/getOrdersBarcode", {uid:cli, list:JSON.stringify([order])})
      if (files?.[0]) {
          setPrintTrigger(`data:image/png;base64,${files[0].file}`)
      }
      else {
        setLoadingPrint(false);
      }
  }, [cli]);

  const handleQzPrint = useCallback(async (order) => {
    try {
      const files = await get("WBAPI/getOrdersBarcode", { uid: cli, list: JSON.stringify([order]) });
      if (!files?.[0]?.file) {
        showNotification({ type: "error", message: "Не удалось получить этикетку" });
        return;
      }
      const data = [{ type: "pixel", format: "image", flavor: "base64", data: files[0].file }];
      await printLocal(data, {
        size: { width: 58, height: 40 },
        units: "mm",
        orientation: "portrait",
        margins: 0,
        scaleContent: true,
        rasterize: true,
      });
    } catch (error) {
      console.error("QZ Tray print error:", error);
      showNotification({ type: "error", message: "Не удалось распечатать этикетку" });
    }
  }, [cli, printLocal, showNotification]);

  useEffect(() => {
      if (!printTrigger) {
          return;
      }

      const runPrint = async () => {
          await new Promise(resolve => setTimeout(resolve, 100));
          startPrint();
      };

      runPrint();
  }, [printTrigger, startPrint]);

  // @ts-ignore
  const handleCopy = useCallback(async (text) => {
    const success = await copy(text);
    if (success) {
      showNotification({
        id: 'copyCodeSucces',
        type: 'success',
        message: 'Код скопирован'
      });
    }
  }, [copy, showNotification]);

  const handleDownload = () => {
    const FILE_ID = '1ghm5LVYmVSIus3u4kpnP2cH0YtLAUeAI';
    const downloadUrl = `https://drive.google.com/uc?export=download&id=${FILE_ID}`;
    
    // Открываем ссылку в новой вкладке
    window.open(downloadUrl, '_blank');
  };

  const rowVirtualizer = useVirtualizer({
      count: allVisibleCodes.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => ROW_HEIGHT,
      overscan: 5,                       // сколько строк рендерить за пределами окна
      getItemKey: (index) => allVisibleCodes[index].full,   // ключ = id товара
  });

  const addedCount = selectedBC
    ? (codes[selectedBC]?.length ?? 0)
    : allCodes.length;

  const totalCount = selectedBC
    ? (orderBarcodesIndex.get(selectedBC)?.length ?? 0)
    : order_barcodes.length;

  return (
    <Box
      onClick={handleContainerClick}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Верхняя панель: ввод + выбор элемента bclist */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          marginBottom: '10px',
          marginLeft: '10px',
          width: 'calc(100% - 20px)',
          justifyContent: 'space-between'
        }}
      >
        <TextField
          inputRef={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Просканируйте код"
          variant="standard"
          onBlur={() => setTimeout(focusInput, 100)}
          autoComplete="off"
          sx={{
            width: '40%',
            borderRadius: '10px',
            backgroundColor: '#bddbdb',
            color: 'white',
            paddingLeft: '16px',
            '& .MuiInput-underline': {
              '&:before': {
                borderBottom: 'none'
              },
              '&:after': {
                borderBottom: 'none'
              },
              '&:hover:not(.Mui-disabled):before': {
                borderBottom: 'none'
              }
            }
          }}
        />
        
        {/* Блок с чекбоксами и селектом */}
        <div style={{width: "55%", display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: '16px'}}>
          {/* Чекбокс Звук */}
          <FormControlLabel
            control={
              <Checkbox
                checked={soundEnabled}
                onChange={(e) => setSoundEnabled(e.target.checked)}
                sx={{
                  color: '#bddbdb',
                  '&.Mui-checked': {
                    color: '#bddbdb',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#333', fontSize: '14px' }}>
                Звук
              </Typography>
            }
          />
          
          {/* Чекбокс Автопечать */}
          <FormControlLabel
            control={
              <Checkbox
                checked={autoPrintEnabled}
                onChange={(e) => setAutoPrintEnabled(e.target.checked)}
                sx={{
                  color: '#bddbdb',
                  '&.Mui-checked': {
                    color: '#bddbdb',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#333', fontSize: '14px' }}>
                Автопечать
              </Typography>
            }
          />

          <Tooltip title={"Скачать приложение для автопечати"}>
            <IconButton
              onClick={handleDownload}
            >
              <FileDownloadIcon style={{color: iconColor}}/>
            </IconButton>
          </Tooltip>

          <Typography>
            Добавлено {addedCount}/{totalCount}
          </Typography>
          
          <Select
              displayEmpty
              value={selectedBC}
              onChange={(e) => {
                setSelectedBC(e.target.value);
                setInputValue('');
                setTimeout(focusInput, 0);
              }}
              sx={{
                  borderRadius: "10px", 
                  height: "30px", 
                  width: "40%",
                  backgroundColor: '#bddbdb',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  }
              }}
              MenuProps={{
                  PaperProps: {
                      sx: {
                          maxHeight: "400px",
                          overflowY: 'auto'
                      }
                  }
              }}
          >
              <MenuItem key={'bc_all'} value={undefined} style={{ borderRadius: '10px', marginBottom: '5px' }}>
                  <Typography variant="body1" sx={{ ml: 1}}>
                    {'Все'}
                  </Typography>
              </MenuItem> 
              {bclist.map(sel => 
                  <MenuItem key={sel} value={sel} style={{ borderRadius: '10px', marginBottom: '5px' }}>
                      <Typography variant="body1" sx={{ ml: 1}}>
                        {sel}     
                      </Typography>
                  </MenuItem> 
              )}
          </Select>
        </div>
      </Box>
      <div
          ref={parentRef}
          className="custom-scrollbar"
          style={{
              height: 'calc(100% - 60px)', 
              overflow: 'auto',
              width: '100%',
              borderRadius: '10px',
              marginTop: '1%',
          }}
      >
          <div
              style={{
                  height: rowVirtualizer.getTotalSize(),   // ← фантомная высота
                  width: '98%',
                  margin: '0 1%',
                  position: 'relative',
              }}
          >
              {rowVirtualizer.getVirtualItems().map((vi) => {
                  const item = allVisibleCodes[vi.index];
                  const status = qrChecked?.[item.order];
                  return (
                      <div
                          key={item.full}
                          style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: ROW_HEIGHT,
                              transform: `translateY(${vi.start}px)`,
                          }}
                      >
                          <CodeCard
                            // @ts-ignore
                            item={item}
                            statusText={status?.text}
                            statusColor={status?.color}
                            statusLoading={qrChecked === undefined}
                            handleCopy={handleCopy}
                            handleDelete={handleDelete}
                            handlePrint={handlePrint}
                            first={vi.index === 0}
                          />
                      </div>
                  );
              })}
          </div>
      </div>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          width: 'calc(100% - 20px)',
          margin: '0px 10px 0px 10px',
          justifyContent: 'space-between',
          alignItems: 'flex-end'
        }}
      >
        <CancelButton
          action={cancel}
          text={'ОТМЕНА'}
        />

        <OKButton
          action={saveAction}
          text={'СОХРАНИТЬ'}
        />
      </Box>
      <div
          style={{
              position: 'fixed',
              left: '-10000px',
              top: 0,
              width: '100%',
              visibility: 'visible',
              pointerEvents: 'none'
          }}
      >
          <div ref={printContainerRef}>
              {printTrigger && (
                  <div className="barcode-print-page" style={{ width: '58mm', height: '40mm' }}>
                      <img 
                          src={printTrigger} 
                          style={{ 
                              width: '100%', 
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block'
                          }} 
                      />
                  </div>
              )}
          </div>
      </div>
    </Box>
  );
};

const CodeCard = memo(function CodeCard({
      // @ts-ignore
      item,
      // @ts-ignore
      handleCopy,
      // @ts-ignore
      handleDelete,
      // @ts-ignore
      statusText,
      // @ts-ignore
      statusColor,
      // @ts-ignore
      statusLoading,
      // @ts-ignore
      handlePrint,
      // @ts-ignore
      first = false
  }) {
      return (
          <Card
            sx={{
              borderRadius: '10px',
              margin: '10px',
              boxShadow: '0px 0px 5px rgba(0,0,0,0.2)',
              borderColor: '#065f52',
              borderStyle: 'solid',
              borderWidth: first? '2px': "0px",
            }}
          >
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                <Box sx={{width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'}}>
                  {/* Секции с подписями сверху */}
                  <Box
                    display="flex"
                    // flexWrap="wrap"
                    gap={1}
                    mb={1}
                  >
                    {/* {['01', '21', '91', '92'].map((key) => { */}
                    {['01', '21'].map((key) => {
                      const value = item.parsed[key];
                      const color = getSectionColor(key, value);
                      const label = getSectionLabel(key);
                      const display = getSectionDisplay(key, value);

                      return (
                        <Box
                          key={key}
                          component="span"
                          sx={{
                            mr: 1
                          }}
                        >
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{
                              fontWeight: 'bold',
                              color
                            }}
                          >
                            {label}:
                          </Typography>

                          <Typography
                            variant="body2"
                            component="span"
                            sx={{
                              fontFamily: 'monospace',
                              color: 'gray'
                            }}
                          >
                            {display}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  <div>
                      <IconButton
                        aria-label="menu"
                        onClick={() => {
                          handleDelete(item.full, item.order);
                        }}
                        sx={{
                          marginRight: '10px'
                        }}
                      >
                        <DeleteForeverIcon
                          sx={{
                            transition: 'all 0.3s ease',
                            '&:hover': hoverStyle,
                            color: iconColor
                          }}
                        />
                      </IconButton>
                      <IconButton
                        aria-label="menu"
                        onClick={() => {
                          handlePrint(item.order);
                        }}
                        sx={{
                          marginRight: '10px'
                        }}
                      >
                        <PrintIcon
                          sx={{
                            transition: 'all 0.3s ease',
                            '&:hover': hoverStyle,
                            color: iconColor
                          }}
                        />
                      </IconButton>
                  </div>
                </Box>

                {/* Полный код */}
                <div style={{display: 'flex', flexDirection: 'row', alignItems: 'flex-start'}}>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'nowrap',
                      color: 'gray',
                      mt: '1px'
                    }}
                  >
                    Полный код:
                  </Typography>
                  <Button
                    variant="text"
                    sx={{
                      display: 'block',
                      maxWidth: '100%',        // или конкретное значение, например '300px'
                      color: 'gray',
                      fontSize: '14px',
                      padding: 0,
                      margin: 0,
                      textTransform: 'none',
                      ml: '5px',
                      textAlign: 'left',
                      minWidth: 0,             // важно для flex-контейнеров
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      '& .MuiButton-label': {
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                    onClick={() => { handleCopy(item.full) }}
                  >
                    {item.full}
                  </Button>
                </div>
                <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: "10px"}}>
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: 'break-all',
                        color: 'gray'
                      }}
                    >
                      Заказ: {item.order}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: 'break-all',
                        fontWeight: 'bold',
                        color: statusLoading ? "gray" : (statusColor || "gray")
                      }}
                    >
                      Статус: {statusLoading ? "загрузка" : (statusText || "еще не проверялся")}
                    </Typography>
                </div>
              </Box>

            </CardContent>
          </Card>
      );
  });

export const ScannerBind = ({ openPallet, openBox, addToBox, addrs }) => {

  console.log("scanner addrs", addrs)

  /** @type {[any, Function]} */
  const [inputValue, setInputValue] = useState('');
  /** @type {[any, Function]} */
  const [addr, setAddr] = useState(undefined);
  /** @type {[any, Function]} */
  const [box, setBox] = useState(undefined);

  /** @type {any} */
  const inputRef = useRef(null);

  // ----- Ключи для localStorage -----
  const STORAGE_KEYS = {
    SOUND: 'scanner_split_sound_enabled',
  };

  // ----- Состояния для чекбоксов с загрузкой из localStorage -----
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.SOUND);
    return saved !== null ? JSON.parse(saved) : true;
  });

  // ----- Сохранение в localStorage при изменении -----
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.SOUND, JSON.stringify(soundEnabled));
  }, [soundEnabled]);

  const { showNotification } = useLocalNotifications();
  // @ts-ignore
  const preventDoubleClick = usePreventDoubleClick();
  const isCapsLockOn = useCapsLock();
  // @ts-ignore
  const { copy, copied } = useCopyToClipboard();

  const { playSuccess, playError } = useSound('/sounds/scan-success.mp3', '/sounds/scan-error.mp3');


  // Фокус при монтировании
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const focusInput = () => {
      requestAnimationFrame(() => {
          setTimeout(() => {
              inputRef.current?.focus();
          }, 0);
      });
  };


  const isAddr = (text) => {
    // Проверяем формат адреса: буква (латинская большая) - число - число
    const addrRegex = /^[A-Z]-[0-9]+-[0-9]+$/;
    
    if (!addrRegex.test(text)) {
      return false;
    }
    
    // Проверяем, есть ли адрес в списке addrs
    return Array.isArray(addrs) && addrs.includes(text);
  };

  const convertToBoxTitle = (text) => {
    // Разбираем формат: Кчисло-Счисло-кчисло
    const match = text.match(/^([0-9]+)-([0-9]+)-([0-9]+)$/);
    
    if (!match) {
      return text;
    }
    
    const [, kValue, sValue, korpusValue] = match;
    
    // Убираем ведущие нули
    const b = parseInt(kValue, 10);
    const d = parseInt(sValue, 10);
    const c = parseInt(korpusValue, 10);
    
    return `С${d}_к${c}_К${b}`;
  };

  const isBox = (text) => {
    // Формат 1: Счисло_кчисло_Кчисло (например, С37_к3_К2)
    const format1Regex = /^С[0-9]+_к[0-9]+_К[0-9]+$/;
    
    if (format1Regex.test(text)) {
      return true;
    }
    
    // Формат 2: число-число-число (например, 1-0037-0003)
    const format2Regex = /^[0-9]+-[0-9]+-[0-9]+$/;
    
    if (format2Regex.test(text)) {
      return true;
    }
    
    return false;
  };

  // ----- Добавление кода в список -----
  const handleAddCode = async () => {
    let trimmed = inputValue.trim();

    if (trimmed === '') return;

    if (isCapsLockOn) {
      showNotification({
        id: 'capslockErr',
        type: 'warning',
        message: `Не используйте сканнер с включенным CapsLock`
      });
      if (soundEnabled) playError();
      setInputValue('');
      focusInput();
      return
    }

    // Проверяем, является ли введенное значение баркодом из списка
    if (isAddr(trimmed)) {
      openPallet(trimmed)
      showNotification({
        id: 'addr-switch',
        type: 'info',
        message: `Переключено на ячейку: ${trimmed}`
      });
      if (soundEnabled) playSuccess();
      setAddr(trimmed)
      setInputValue('');
      focusInput();
      return;
    }

    if (isBox(trimmed)) {
      trimmed = convertToBoxTitle(trimmed)
      if (addr) {
        let err = openBox(addr, trimmed)
        if (!err) {
          showNotification({
            id: 'box-switch',
            type: 'info',
            message: `Выбран короб: ${trimmed}`
          });
          if (soundEnabled) playSuccess();
          setBox(trimmed)
          setInputValue('');
          focusInput();
          return;
        }
        else {
          showNotification({
            id: 'box-switch-otherAddr',
            type: 'warning',
            message: err
          });
          if (soundEnabled) playError();
          setInputValue('');
          focusInput();
          return;
        }
      }
      else {
        showNotification({
          id: 'box-switch-er',
          type: 'warning',
          message: `Ячейка не выбрана`
        });
        if (soundEnabled) playError();
        setInputValue('');
        focusInput();
        return;
      }
    }

    if (addr) {
      let err = addToBox(addr, box, trimmed)
      if (err) {
        showNotification({
          id: 'box-switch-er',
          type: 'warning',
          message: err
        });
        if (soundEnabled) playError();
        setInputValue('');
        focusInput();
        return;
      }
      else {
        //Опевещение о добавлении товара и так есть в самой привязке
        if (soundEnabled) playSuccess();
        setInputValue('');
        focusInput();
        return;
      }
    }
    else {
      showNotification({
        id: 'box-switch-er',
        type: 'warning',
        message: `Ячейка не выбрана`
      });
      if (soundEnabled) playError();
      setInputValue('');
      focusInput();
      return;
    }

  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAddCode();
    }
  };

  const handleContainerClick = () => {
    focusInput();
  };


  return (
    <Box
      onClick={handleContainerClick}
      sx={{
        width: '500px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Верхняя панель: ввод + выбор элемента bclist */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '95%',
          justifyContent: 'space-between'
        }}
      >
        <TextField
          inputRef={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Просканируйте код"
          variant="standard"
          onBlur={() => setTimeout(focusInput, 100)}
          autoComplete="off"
          sx={{
            width: '40%',
            borderRadius: '10px',
            backgroundColor: '#bddbdb',
            color: 'white',
            paddingLeft: '16px',
            '& .MuiInput-underline': {
              '&:before': {
                borderBottom: 'none'
              },
              '&:after': {
                borderBottom: 'none'
              },
              '&:hover:not(.Mui-disabled):before': {
                borderBottom: 'none'
              }
            },
            '& input::placeholder': {
              color: 'white',
              opacity: 1, // Firefox по умолчанию делает плейсхолдер полупрозрачным
            },
          }}
        />
        
        
        <Typography variant="body2" sx={{ color: '#333', fontSize: '14px', width: '55%', textAlign: 'right'}}>
          {`Ячейка: ${addr || ""} ${box? "Короб:": ""}${box || ""}`}
        </Typography>

      </Box>
      <div style={{display: 'flex', flexDirection: 'row'}}>
        {/* Чекбокс Звук */}
        <FormControlLabel
          control={
            <Checkbox
              checked={soundEnabled}
              onChange={(e) => setSoundEnabled(e.target.checked)}
              sx={{
                color: '#bddbdb',
                '&.Mui-checked': {
                  color: '#bddbdb',
                },
                marginLeft: "8px"
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: '#333', fontSize: '14px' }}>
              Звук
            </Typography>
          }
        />
      </div>
    </Box>
  );
};

